import { Box, Stack, TextField, Typography, useTheme } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { HttpStatusCode } from 'axios';
import { useFormik } from 'formik';
import { join } from 'lodash';
import { useEffect } from 'react';
import { Search } from 'react-feather';
import { useNavigate } from 'react-router-dom';
import ButtonWithLoading from 'src/components/common/ButtonWithLoading';
import FormikForm from 'src/components/common/FormikForm';
import FormAutoCompleteByCountry from 'src/components/Form/FormAutoCompleteByCountry';
import PublicService from 'src/services/api/public';
import { COUNTRIES, TypeCountry } from 'src/utils/constants/countries';
import { NoAuthRouteKeys } from 'src/utils/constants/routes';
import { SUCCESS_CODE } from 'src/utils/constants/statusCode';
import { sortObjectsArrayByKey } from 'src/utils/helpers/arrayHelpers';
import { useSingleHSCodePredictionStore } from 'src/zustand/useAILabelingStore';
import { IFrameType, usePopupRedirectStore } from 'src/zustand/usePopupRedirectStore';

const FormSingleSearch = () => {
  const { dispatch, productName, productDescription, singlePredictions, country } = useSingleHSCodePredictionStore();

  const navigate = useNavigate();
  useEffect(() => {
    if (singlePredictions.status_code === SUCCESS_CODE.GET) {
      navigate(NoAuthRouteKeys.AIHSCodeSearchResultPublic);
    }
  }, [singlePredictions.status_code]);

  const { dispatch: dispatchPopup } = usePopupRedirectStore();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: PublicService.getSentencePrediction,
  });

  const {
    palette: { textTertiary },
  } = useTheme();

  const formik = useFormik<{ sentence?: string; country_code?: string }>({
    initialValues: {},
    async onSubmit() {
      if (!country) return;
      const sentence = join([productName, productDescription], ' ');
      const payload = {
        sentence: sentence,
        country_code: country.code,
      };
      const res = await mutateAsync(payload);
      if (res.status_code === HttpStatusCode.TooManyRequests) {
        dispatchPopup({ visible: true, iframe: IFrameType.AiHsCodeLabeling });
      }
      dispatch(res, 'singlePredictions');
    },
  });

  return (
    <FormikForm formik={formik}>
      <Stack spacing={6} mt={6}>
        <Box>
          <Stack direction={'row'}>
            <Typography mb={1} variant="h6">
              Instant HS Code Lookup
            </Typography>
          </Stack>

          <Typography color={textTertiary.main} variant="body2">
            Start by entering your Product Name or a brief description. Our AI will analyze the information and quickly
            predict the most accurate HS8 code, saving you time and effort
          </Typography>
        </Box>

        <Stack spacing={3}>
          <Stack>
            <FormAutoCompleteByCountry
              id={'product_country'}
              countryCodeField={'code'}
              getOptionKeyFn={(option) => `${option['code']}-${option['phone']}`}
              labelField={'label'}
              required
              value={country}
              placeholder={"Product's Country"}
              countryCode={country?.code}
              onChange={(value: TypeCountry) => {
                dispatch(value, 'country');
              }}
              options={sortObjectsArrayByKey(COUNTRIES, 'label')}
            />
          </Stack>
          <TextField
            onChange={(e) => dispatch(e.target.value, 'productDescription')}
            required
            multiline
            rows={12}
            size="small"
            label="Please Describe Your Insights About This Product"
          />
        </Stack>

        <ButtonWithLoading
          isLoading={isPending}
          type="submit"
          isRounded
          size="large"
          variant="contained"
          endIcon={<Search size={18} />}
        >
          Ai Match
        </ButtonWithLoading>
      </Stack>
    </FormikForm>
  );
};

export default FormSingleSearch;
